import { useMemo, useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import {
    WEEKDAYS,
    WEEKDAY_GROUPS,
    addDayPeriod,
    applySourceDayToDays,
    formatDayPeriods,
    normalizeOperatingHours,
    removeDayPeriod,
    setDayOpen,
    updateDayPeriod,
} from "@/lib/operatingHours";

function CopyHoursBar({ hours, onChange, testIdPrefix }) {
    const normalized = normalizeOperatingHours(hours);
    const firstOpenDay = normalized.find((row) => row.open) || normalized[0];
    const [sourceDay, setSourceDay] = useState(firstOpenDay?.day || "Mon");
    const [targetDays, setTargetDays] = useState(() => new Set());

    const sourceRow = normalized.find((row) => row.day === sourceDay) || firstOpenDay;
    const sourceSummary = formatDayPeriods(sourceRow);
    const targetList = useMemo(
        () => WEEKDAYS.filter((day) => day !== sourceDay && targetDays.has(day)),
        [sourceDay, targetDays],
    );

    const toggleTarget = (day) => {
        if (day === sourceDay) return;
        setTargetDays((current) => {
            const next = new Set(current);
            if (next.has(day)) next.delete(day);
            else next.add(day);
            return next;
        });
    };

    const selectGroup = (groupKey) => {
        setTargetDays(new Set(WEEKDAY_GROUPS[groupKey].filter((day) => day !== sourceDay)));
    };

    const clearTargets = () => setTargetDays(new Set());

    const applyCopy = () => {
        if (!targetList.length) {
            toast.error("Select at least one other day");
            return;
        }
        onChange(applySourceDayToDays(hours, sourceDay, targetList));
        toast.success(`Copied ${sourceDay} to ${targetList.length} day${targetList.length === 1 ? "" : "s"}`);
    };

    const applyLabel = targetList.length
        ? `Apply to ${targetList.length} day${targetList.length === 1 ? "" : "s"} — ${targetList.join(", ")}`
        : "Select days to apply";

    return (
        <section
            className="rounded-2xl border border-[#E8E2D8] bg-white p-5 shadow-sm"
            data-testid={`${testIdPrefix}-copy-bar`}
        >
            <p className="text-base font-extrabold text-[#1A1A1A]" data-testid={`${testIdPrefix}-copy-bar-title`}>
                Copy hours to other days
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
                Pick a day to copy from, then choose which days should match it.
            </p>

            <div className="mt-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                    Copy hours from
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-3 rounded-xl bg-[#F7F3EC] px-3 py-2.5">
                    <label htmlFor={`${testIdPrefix}-copy-source-day`} className="sr-only">
                        Copy hours from
                    </label>
                    <select
                        id={`${testIdPrefix}-copy-source-day`}
                        data-testid={`${testIdPrefix}-copy-source-day`}
                        className="h-10 min-w-[5.5rem] rounded-lg border border-[#E5DFD4] bg-white px-3 text-sm font-bold text-[#1A1A1A]"
                        value={sourceDay}
                        onChange={(event) => {
                            const nextSource = event.target.value;
                            setSourceDay(nextSource);
                            setTargetDays((current) => {
                                const next = new Set(current);
                                next.delete(nextSource);
                                return next;
                            });
                        }}
                    >
                        {WEEKDAYS.map((day) => (
                            <option key={day} value={day}>{day}</option>
                        ))}
                    </select>
                    <p
                        className="min-w-0 flex-1 text-sm font-medium text-[#4A4A4A]"
                        data-testid={`${testIdPrefix}-copy-source-summary`}
                    >
                        {sourceSummary}
                    </p>
                </div>
            </div>

            <div className="mt-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                    Copy hours to
                </p>
                <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-copy-target-chips`}>
                    {WEEKDAYS.map((day) => {
                        const dayKey = day.toLowerCase();
                        const isSource = day === sourceDay;
                        const selected = !isSource && targetDays.has(day);
                        return (
                            <button
                                key={day}
                                type="button"
                                disabled={isSource}
                                data-testid={`${testIdPrefix}-copy-target-${dayKey}`}
                                aria-pressed={selected}
                                className={`tap-target inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2.5 text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:border-[#E8E2D8] disabled:bg-[#F3F0EA] disabled:text-[#9A948A] disabled:opacity-100 ${
                                    selected
                                        ? "border-primary bg-primary text-primary-foreground"
                                        : "border-[#E5DFD4] bg-white text-[#1A1A1A] hover:border-primary/40"
                                }`}
                                onClick={() => toggleTarget(day)}
                            >
                                {selected ? <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden /> : null}
                                {day}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full border-[#E5DFD4] bg-white px-3.5"
                        data-testid={`${testIdPrefix}-copy-select-all`}
                        onClick={() => selectGroup("all")}
                    >
                        Select all
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full border-[#E5DFD4] bg-white px-3.5"
                        data-testid={`${testIdPrefix}-copy-select-weekdays`}
                        onClick={() => selectGroup("weekdays")}
                    >
                        Weekdays
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full border-[#E5DFD4] bg-white px-3.5"
                        data-testid={`${testIdPrefix}-copy-select-weekend`}
                        onClick={() => selectGroup("weekend")}
                    >
                        Weekend
                    </Button>
                    <button
                        type="button"
                        className="px-1 text-sm font-bold text-primary hover:underline"
                        data-testid={`${testIdPrefix}-copy-clear`}
                        onClick={clearTargets}
                    >
                        Clear
                    </button>
                </div>
            </div>

            <Button
                type="button"
                className="tap-target mt-5 h-12 w-full rounded-xl text-base font-bold"
                data-testid={`${testIdPrefix}-copy-apply-button`}
                disabled={!targetList.length}
                onClick={applyCopy}
            >
                {applyLabel}
            </Button>
        </section>
    );
}

export function HoursEditor({ hours, onChange, compact = false, showCopyBar = !compact, testIdPrefix = "hours" }) {
    const normalized = normalizeOperatingHours(hours);

    return (
        <div className={compact ? "space-y-2" : "space-y-3"} data-testid={`${testIdPrefix}-editor`}>
            {showCopyBar ? <CopyHoursBar hours={hours} onChange={onChange} testIdPrefix={testIdPrefix} /> : null}
            {normalized.map((row) => {
                const dayKey = row.day.toLowerCase();
                return (
                    <div
                        key={row.day}
                        className="rounded-lg border p-3"
                        data-testid={`${testIdPrefix}-row-${dayKey}`}
                    >
                        <div className="flex items-center justify-between gap-3">
                            <strong data-testid={`${testIdPrefix}-day-${dayKey}`}>{row.day}</strong>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-muted-foreground">Open</span>
                                <Switch
                                    data-testid={`${testIdPrefix}-toggle-${dayKey}`}
                                    checked={row.open}
                                    onCheckedChange={(checked) => onChange(setDayOpen(hours, row.day, checked))}
                                />
                            </div>
                        </div>

                        {row.open ? (
                            <div className="mt-3 space-y-2">
                                {row.periods.map((period, periodIndex) => (
                                    <div
                                        key={`${row.day}-${periodIndex}`}
                                        className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2"
                                        data-testid={`${testIdPrefix}-period-${dayKey}-${periodIndex}`}
                                    >
                                        <Input
                                            data-testid={`${testIdPrefix}-open-${dayKey}-${periodIndex}`}
                                            value={period.from}
                                            placeholder="10:00"
                                            onChange={(event) => onChange(
                                                updateDayPeriod(hours, row.day, periodIndex, "from", event.target.value)
                                            )}
                                        />
                                        <span className="text-sm font-bold text-muted-foreground">to</span>
                                        <Input
                                            data-testid={`${testIdPrefix}-close-${dayKey}-${periodIndex}`}
                                            value={period.to}
                                            placeholder="14:00"
                                            onChange={(event) => onChange(
                                                updateDayPeriod(hours, row.day, periodIndex, "to", event.target.value)
                                            )}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-9 w-9 shrink-0"
                                            data-testid={`${testIdPrefix}-remove-period-${dayKey}-${periodIndex}`}
                                            disabled={row.periods.length <= 1}
                                            onClick={() => onChange(removeDayPeriod(hours, row.day, periodIndex))}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="tap-target rounded-lg"
                                    data-testid={`${testIdPrefix}-add-period-${dayKey}`}
                                    onClick={() => onChange(addDayPeriod(hours, row.day))}
                                >
                                    <Plus className="h-4 w-4" />
                                    Add period
                                </Button>
                            </div>
                        ) : (
                            <p className="mt-2 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-closed-${dayKey}`}>
                                Closed all day
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
